import { useState } from "react";
import { ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Img, SIZES } from "@/components/shared/Img";
import { useImageAlts } from "@/hooks/use-image-alts";
import { Lightbox } from "@/components/shared/Lightbox";
import { PhotoCredit } from "@/components/shared/PhotoCredit";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";

export { Lightbox };

/** Caption under a photograph: what we see, then who took it – the quiet editorial line. */
const Caption = ({ text, src, size = "sm", testId }) => {
  if (!text) return null;
  return (
    <figcaption className={cn("mt-2.5 flex items-baseline justify-between gap-4 px-0.5 leading-snug text-muted-foreground", size === "lg" ? "text-[14px] sm:text-[15px]" : "text-[12.5px]")} data-testid={testId}>
      <span className="min-w-0 text-pretty text-foreground/80">{text}</span>
      <PhotoCredit src={src} className="shrink-0" testId={`${testId}-credit`} />
    </figcaption>
  );
};

/** Room gallery: one lead photo, then every other photo at a proper size, each with its caption and credit. Click any to open
 *  the viewer. `leadAlt` / `leadClassName` describe and frame the lead photo only (e.g. object-position for a tall picture). */
export const ImageGallery = ({ images = [], captions = {}, alt = "", leadAlt, leadClassName, testId = "image-gallery" }) => {
  const { altFor } = useImageAlts();
  const [open, setOpen] = useState(null);
  if (!images.length) {
    return (
      <div className="flex aspect-[16/10] w-full items-center justify-center rounded-[12px] border border-dashed border-input text-muted-foreground" data-testid={`${testId}-empty`}>
        <div className="text-center">
          <ImageOff className="mx-auto h-6 w-6" />
          <p className="mt-2 t-mono">Ingen bilete enno</p>
        </div>
      </div>
    );
  }
  const [first, ...rest] = images;
  const cols = rest.length === 1 ? "grid-cols-1" : rest.length === 2 || rest.length === 4 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3";
  const restAspect = rest.length === 1 ? "aspect-[16/9]" : "aspect-[4/3]";
  const textFor = (src, fallback) => captions[src] || altFor(src, fallback);
  return (
    <div data-testid={testId}>
      <figure className="m-0">
        <button type="button" onClick={() => setOpen(0)} className="img-hover relative block w-full overflow-hidden rounded-[12px] bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2" aria-label={captions[first] ? `${captions[first]} – opne i full storleik` : "Opne bilete i full storleik"} data-testid={`${testId}-main-button`}>
          <Img src={first} alt={captions[first] || leadAlt || altFor(first, alt)} width={1600} sizes={SIZES.twoThirds} priority className={cn("aspect-[3/2] w-full object-cover sm:aspect-[16/9]", leadClassName)} data-testid={`${testId}-main`} />
          {!captions[first] && <PhotoCredit src={first} overlay testId={`${testId}-main-credit`} />}
          <PhotoNumberTag src={first} className="absolute right-2.5 top-2.5" testId={`${testId}-main-number`} />
        </button>
        <Caption text={captions[first]} src={first} size="lg" testId={`${testId}-main-caption`} />
      </figure>
      {rest.length > 0 && (
        <div className={cn("mt-4 grid gap-x-3 gap-y-5", cols)}>
          {rest.map((src, i) => (
            <figure key={src} className="m-0 min-w-0">
              <button type="button" onClick={() => setOpen(i + 1)} className="img-hover relative block w-full overflow-hidden rounded-[12px] bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2" aria-label={captions[src] ? `${captions[src]} – bilete ${i + 2} av ${images.length}` : `Bilete ${i + 2} av ${images.length}`} data-testid={`${testId}-thumb-${i}`}>
                <Img src={src} alt={textFor(src, `${alt} – bilete ${i + 2}`)} width={800} sizes="(min-width: 1024px) 300px, 50vw" className={cn("w-full object-cover", restAspect)} />
                {!captions[src] && <PhotoCredit src={src} overlay testId={`${testId}-thumb-${i}-credit`} />}
                <PhotoNumberTag src={src} className="absolute right-2 top-2" testId={`${testId}-thumb-${i}-number`} />
              </button>
              <Caption text={captions[src]} src={src} testId={`${testId}-thumb-${i}-caption`} />
            </figure>
          ))}
        </div>
      )}
      <p className="mt-4 t-mono tabular text-muted-foreground">{images.length} bilete</p>
      <Lightbox images={images} index={open} onChange={setOpen} alt={alt} captions={captions} testId={testId} />
    </div>
  );
};

export const LightboxGrid = ({ images = [], captions = {}, testId = "lightbox-grid" }) => {
  const [open, setOpen] = useState(null);
  if (!images.length) return null;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid={testId}>
        {images.map((src, i) => (
          <button key={`${src}-${i}`} type="button" className={cn("img-hover relative block w-full overflow-hidden rounded-[12px] bg-secondary", i === 0 && "col-span-2 row-span-2")} onClick={() => setOpen(i)} aria-label={captions[src] || `Bilete ${i + 1} av ${images.length}`} data-testid={`${testId}-item-${i}`}>
            <Img src={src} alt="" width={800} sizes={SIZES.third} className="aspect-[4/3] h-full w-full object-cover" />
            {captions[src] && (
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 via-ink/40 to-transparent px-4 pb-3 pt-12 text-left sm:px-5 sm:pb-4" data-testid={`${testId}-caption-${i}`}>
                <span className={cn("block font-display font-medium leading-snug tracking-[-0.01em] text-paper [text-shadow:0_1px_12px_rgba(0,0,0,.4)]", i === 0 ? "text-[15px] sm:text-[18px]" : "text-[13px] sm:text-[14px]")}>{captions[src]}</span>
                <PhotoCredit src={src} className="mt-1 !text-paper/70" testId={`${testId}-credit-${i}`} />
              </span>
            )}
            {!captions[src] && <PhotoCredit src={src} overlay testId={`${testId}-credit-${i}`} />}
            <PhotoNumberTag src={src} className="absolute right-2 top-2" testId={`${testId}-number-${i}`} />
          </button>
        ))}
      </div>
      <Lightbox images={images} index={open} onChange={setOpen} captions={captions} testId={testId} />
    </>
  );
};
